import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { CanvasSettings } from "../../../editor/types";
import { useLocale } from "../../../locales";
import type {
  Lesson,
  Grade,
  Subject,
  QuestionType,
  EducationalMaterialData,
} from "../../../services/api";
import { apiService } from "../../../services/api";
import EducationalMaterialModal from "./EducationalMaterialModal";

type QuestionMeta = NonNullable<CanvasSettings["question"]>;

interface QuestionSettingsBarProps {
  canvas: CanvasSettings;
  onUpdateQuestion: (patch: Partial<QuestionMeta>) => void;
  lessons: Lesson[];
  grades: Grade[];
  subjects: Subject[];
  questionTypes: QuestionType[];
  isLoading: boolean;
}

const QuestionSettingsBar: React.FC<QuestionSettingsBarProps> = ({
  canvas,
  onUpdateQuestion,
  lessons,
  grades,
  subjects,
  questionTypes,
  isLoading,
}) => {
  const { t } = useLocale();
  const question = canvas.question;

  const [educationalMaterials, setEducationalMaterials] =
    useState<EducationalMaterialData | null>(null);
  const [isEducationalMaterialModalOpen, setIsEducationalMaterialModalOpen] =
    useState(false);
  const [educationalMaterialModalType, setEducationalMaterialModalType] =
    useState<"video" | "others">("video");
  const [educationalMaterialContext, setEducationalMaterialContext] = useState<{
    onSelect: (url: string) => void;
    currentUrl?: string;
  } | null>(null);
  const [previewState, setPreviewState] = useState<{
    isOpen: boolean;
    title: string;
    url: string;
    type: "video" | "text";
  }>({ isOpen: false, title: "", url: "", type: "video" });

  const openPreview = (payload: {
    title: string;
    url: string;
    type: "video" | "text";
  }) => {
    if (!payload.url.trim()) {
      return;
    }
    setPreviewState({ isOpen: true, ...payload });
  };

  const closePreview = () =>
    setPreviewState({ isOpen: false, title: "", url: "", type: "video" });

  const normalizeUrl = (value: string) => value.trim();

  const buildYoutubeEmbedUrl = (value: string) => {
    try {
      const url = new URL(value);
      if (url.hostname.includes("youtu.be")) {
        const videoId = url.pathname.replace("/", "");
        return videoId ? `https://www.youtube.com/embed/${videoId}` : value;
      }
      if (url.hostname.includes("youtube.com")) {
        const videoId = url.searchParams.get("v");
        return videoId ? `https://www.youtube.com/embed/${videoId}` : value;
      }
      return value;
    } catch {
      return value;
    }
  };

  const isDirectVideoUrl = (value: string) =>
    /\.(mp4|webm|ogg)(\?|#|$)/i.test(value);

  // Helper to build the subject hierarchy path from a deepest subject ID
  const buildSubjectPath = useCallback(
    (
      subjectId: string | undefined,
      allSubjects: Subject[]
    ): { subject: string; subTopic: string } => {
      if (!subjectId || !allSubjects || allSubjects.length === 0) {
        return { subject: "", subTopic: "" };
      }

      const subjectMap = new Map<string, Subject>();
      const buildMap = (nodes: Subject[]) => {
        for (const node of nodes) {
          subjectMap.set(node.id, node);
          if (node.children && node.children.length > 0) {
            buildMap(node.children);
          }
        }
      };
      buildMap(allSubjects);

      const path: Subject[] = [];
      let currentId = subjectId;
      while (currentId) {
        const subject = subjectMap.get(currentId);
        if (!subject) break;
        path.unshift(subject);
        currentId = subject.parent_subject_id || "";
      }

      if (path.length === 0) {
        return { subject: "", subTopic: "" };
      }

      return {
        subject: path[0].id,
        subTopic: path.length > 1 ? path[path.length - 1].id : "",
      };
    },
    []
  );

  // Derived UI values (single source of truth: canvas.question)
  const lesson = question?.lesson || "";
  const grade = question?.grade || "";
  const { subject, subTopic } = useMemo(
    () => buildSubjectPath(question?.subject, subjects),
    [buildSubjectPath, question?.subject, subjects]
  );
  const outcome = question?.learningOutcomes || "";
  const difficulty = question?.difficulty ?? 50;
  const solutionVideo = question?.solutionVideoUrl || "";
  const topicVideo = question?.topicVideoUrl || "";
  const solutionTextUrl = question?.solutionTextUrl || "";
  const timeLimit =
    question?.timeLimitSeconds && question.timeLimitSeconds > 0
      ? String(question.timeLimitSeconds)
      : "";
  const trackTime = question?.trackTime ?? false;
  const notMobileFriendly = question?.notMobileFriendly ?? false;
  const displayInMba = question?.displayInMba ?? false;

  const selectedQuestionTypeName = useMemo(() => {
    if (!question?.questionType?.id) {
      return question?.questionType?.name || t("questionSettings.notSelected");
    }
    const matched = questionTypes.find(
      (type) => String(type.id) === String(question?.questionType?.id)
    );
    return (
      matched?.displayName ||
      matched?.name ||
      question?.questionType?.name ||
      t("questionSettings.notSelected")
    );
  }, [question?.questionType, questionTypes, t]);

  const availableGrades = useMemo(() => grades || [], [grades]);

  const filteredSubjects = useMemo(() => {
    if (!grade || !lesson || !subjects || subjects.length === 0) {
      return [];
    }
    return subjects.filter((item) => {
      const gradeMatch = String(item.grade_id) === String(grade);
      const lessonMatch = String(item.lesson_id) === String(lesson);
      const isTopLevel = item.parent_subject_id === null;
      return gradeMatch && lessonMatch && isTopLevel;
    });
  }, [lesson, grade, subjects]);

  const findSubjectById = useCallback(
    (subjectList: Subject[], id: string): Subject | null => {
      for (const item of subjectList) {
        if (item.id === id) return item;
        if (item.has_children && item.children?.length > 0) {
          const found = findSubjectById(item.children, id);
          if (found) return found;
        }
      }
      return null;
    },
    []
  );

  const getSubjectLabel = useCallback(
    (item: Subject) => item.displayName || item.name,
    []
  );

  const subTopicOptions = useMemo(() => {
    if (!subjects || subjects.length === 0 || !subject) {
      return [];
    }
    const selectedSubject = findSubjectById(subjects, subject);
    if (!selectedSubject || !selectedSubject.children?.length) {
      return [];
    }
    const options: Array<{ id: string; label: string }> = [];
    const buildOptions = (nodes: Subject[], pathLabels: string[]) => {
      for (const node of nodes) {
        const label = getSubjectLabel(node);
        const nextPath = [...pathLabels, label];
        options.push({ id: node.id, label: nextPath.join(" > ") });
        if (node.children?.length) {
          buildOptions(node.children, nextPath);
        }
      }
    };
    buildOptions(selectedSubject.children, []);
    return options;
  }, [subjects, subject, findSubjectById, getSubjectLabel]);

  const selectedOutcomeSubjectId = useMemo(() => {
    if (subTopic) return subTopic;
    if (!subject) return "";
    if (subTopicOptions.length > 0) return "";
    return subject;
  }, [subTopic, subject, subTopicOptions.length]);

  const filteredOutcomes = useMemo(() => {
    if (!selectedOutcomeSubjectId) return [];
    const found = findSubjectById(subjects, selectedOutcomeSubjectId);
    return found?.outcomes || [];
  }, [selectedOutcomeSubjectId, subjects, findSubjectById]);

  const selectedTopicVideoTitle = useMemo(() => {
    if (!topicVideo || !educationalMaterials?.video) {
      return "";
    }
    const match = educationalMaterials.video.find((item) => {
      if (typeof item === "string") {
        return item === topicVideo;
      }
      return item?.url === topicVideo;
    });
    if (!match || typeof match === "string") {
      return "";
    }
    return match.title?.trim() || "";
  }, [educationalMaterials, topicVideo]);

  // Fetch educational materials when a sub-topic is selected
  useEffect(() => {
    const fetchEducationalMaterials = async () => {
      if (!subTopic) {
        setEducationalMaterials(null);
        return;
      }
      try {
        const response = await apiService.getEducationalMaterials(subTopic);
        setEducationalMaterials(response.data);
      } catch (error) {
        console.error("Failed to fetch educational materials:", error);
        setEducationalMaterials(null);
      }
    };
    fetchEducationalMaterials();
  }, [subTopic]);

  // --- change handlers (write straight through to canvas.question) ---

  const handleLessonChange = (value: string) =>
    onUpdateQuestion({
      lesson: value,
      grade: "",
      subject: "",
      topic: "",
      learningOutcomes: "",
    });

  const handleGradeChange = (value: string) =>
    onUpdateQuestion({
      grade: value,
      subject: "",
      topic: "",
      learningOutcomes: "",
    });

  const handleSubjectChange = (value: string) =>
    onUpdateQuestion({
      subject: value,
      topic: value,
      learningOutcomes: "",
    });

  const handleSubTopicChange = (value: string) => {
    const deepest = value || subject;
    onUpdateQuestion({
      subject: deepest,
      topic: deepest,
      learningOutcomes: "",
    });
  };

  const handleOutcomeChange = (value: string) =>
    onUpdateQuestion({ learningOutcomes: value });

  const handleDifficultyChange = (value: number) =>
    onUpdateQuestion({ difficulty: value });

  const handleTimeLimitChange = (value: string) =>
    onUpdateQuestion({
      timeLimitSeconds:
        Number(value) > 0 ? Math.max(0, Number(value) || 0) : undefined,
    });

  return (
    <div className="qe-qsbar">
      {/* Row 1: taxonomy + difficulty */}
      <div className="qe-qsbar__row">
      {/* Question type (read-only) */}
      <div className="qe-qsbar__field">
        <span className="qe-qsbar__label">
          {t("questionSettings.questionType")}
        </span>
        <div className="qe-input qe-input--small qe-input--readonly qe-qsbar__readonly">
          {selectedQuestionTypeName}
        </div>
      </div>

      {/* Lesson */}
      <div className="qe-qsbar__field">
        <label className="qe-qsbar__label" htmlFor="qsbar-lesson">
          {t("questionSettings.subject")}
        </label>
        <select
          id="qsbar-lesson"
          className="qe-input qe-input--small"
          value={lesson}
          disabled={isLoading}
          onChange={(e) => handleLessonChange(e.target.value)}
        >
          <option value="">
            {isLoading
              ? t("questionSettings.loading")
              : t("questionSettings.select")}
          </option>
          {lessons?.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
      </div>

      {/* Grade */}
      <div className="qe-qsbar__field">
        <label className="qe-qsbar__label" htmlFor="qsbar-grade">
          {t("questionSettings.grade")}
        </label>
        <select
          id="qsbar-grade"
          className="qe-input qe-input--small"
          value={grade}
          disabled={isLoading || !lesson}
          onChange={(e) => handleGradeChange(e.target.value)}
        >
          <option value="">
            {!lesson
              ? t("questionSettings.selectSubjectFirst")
              : isLoading
              ? t("questionSettings.loading")
              : t("questionSettings.select")}
          </option>
          {availableGrades?.map((item) => (
            <option key={item.id} value={item.id}>
              {item.description}
            </option>
          ))}
        </select>
      </div>

      {/* Topic (top-level subject) */}
      <div className="qe-qsbar__field">
        <label className="qe-qsbar__label" htmlFor="qsbar-subject">
          {t("questionSettings.topic")}
        </label>
        <select
          id="qsbar-subject"
          className="qe-input qe-input--small"
          value={subject}
          disabled={isLoading || !lesson || !grade}
          onChange={(e) => handleSubjectChange(e.target.value)}
        >
          <option value="">
            {!lesson || !grade
              ? t("questionSettings.selectSubjectAndGradeFirst")
              : isLoading
              ? t("questionSettings.loading")
              : t("questionSettings.select")}
          </option>
          {filteredSubjects?.map((item) => (
            <option key={item.id} value={item.id}>
              {item.displayName || item.name}
            </option>
          ))}
        </select>
      </div>

      {/* Sub-topic (only when the topic has children) */}
      {subTopicOptions.length > 0 && (
        <div className="qe-qsbar__field">
          <label className="qe-qsbar__label" htmlFor="qsbar-subtopic">
            {t("questionSettings.subtopic")}
          </label>
          <select
            id="qsbar-subtopic"
            className="qe-input qe-input--small"
            value={subTopic}
            disabled={isLoading}
            onChange={(e) => handleSubTopicChange(e.target.value)}
          >
            <option value="">{t("questionSettings.selectOptional")}</option>
            {subTopicOptions.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Objective / outcome */}
      <div className="qe-qsbar__field">
        <label className="qe-qsbar__label" htmlFor="qsbar-outcome">
          {t("questionSettings.objective")}
        </label>
        <select
          id="qsbar-outcome"
          className="qe-input qe-input--small"
          value={outcome}
          disabled={isLoading || !selectedOutcomeSubjectId}
          title={
            filteredOutcomes.find((o) => o.id === outcome)?.description || ""
          }
          onChange={(e) => handleOutcomeChange(e.target.value)}
        >
          <option value="">
            {!subject
              ? t("questionSettings.selectTopicFirst")
              : subTopicOptions.length > 0 && !subTopic
              ? t("questionSettings.selectSubtopicFirst")
              : isLoading
              ? t("questionSettings.loading")
              : t("questionSettings.selectOptional")}
          </option>
          {filteredOutcomes?.map((item) => (
            <option key={item.id} value={item.id}>
              {item.code}
            </option>
          ))}
        </select>
      </div>

      {/* Difficulty */}
      <div className="qe-qsbar__field qe-qsbar__field--difficulty">
        <label className="qe-qsbar__label" htmlFor="qsbar-difficulty">
          {t("questionSettings.difficultyLevel")} {difficulty}
        </label>
        <input
          id="qsbar-difficulty"
          type="range"
          className="qe-qsbar__slider"
          min="1"
          max="100"
          step="1"
          value={difficulty}
          onChange={(e) => handleDifficultyChange(Number(e.target.value))}
        />
      </div>

      </div>

      {/* Row 2: URLs + time limit + toggles */}
      <div className="qe-qsbar__row qe-qsbar__row--urls">
      {/* Solution video URL */}
      <div className="qe-qsbar__field qe-qsbar__field--url">
        <label className="qe-qsbar__label" htmlFor="qsbar-solution-video">
          {t("questionSettings.solutionVideoUrl")}
        </label>
        <div className="qe-qsbar__url-row">
          <input
            id="qsbar-solution-video"
            className="qe-input qe-input--small"
            type="url"
            placeholder="https://..."
            value={solutionVideo}
            onChange={(e) =>
              onUpdateQuestion({ solutionVideoUrl: e.target.value })
            }
          />
          <button
            type="button"
            className="qe-qsbar__mini-button"
            onClick={() =>
              openPreview({
                title: t("questionSettings.videoPreview"),
                url: normalizeUrl(solutionVideo),
                type: "video",
              })
            }
            disabled={!solutionVideo.trim()}
            title={t("questionSettings.preview")}
            aria-label={t("questionSettings.preview")}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" />
              <circle cx="8" cy="8" r="2" />
            </svg>
          </button>
        </div>
      </div>

      {/* Lecture/topic video (media library + URL) */}
      <div className="qe-qsbar__field qe-qsbar__field--url">
        <label className="qe-qsbar__label" htmlFor="qsbar-topic-video">
          {t("questionSettings.lectureVideoUrl")}
        </label>
        <div className="qe-qsbar__url-row">
          <button
            type="button"
            className="qe-qsbar__mini-button qe-qsbar__mini-button--library"
            onClick={() => {
              setEducationalMaterialContext({
                onSelect: (url) => onUpdateQuestion({ topicVideoUrl: url }),
                currentUrl: topicVideo,
              });
              setEducationalMaterialModalType("video");
              setIsEducationalMaterialModalOpen(true);
            }}
            disabled={
              !subTopic ||
              !educationalMaterials ||
              !Array.isArray(educationalMaterials.video) ||
              educationalMaterials.video.length === 0
            }
            title={
              selectedTopicVideoTitle || t("props.videoSelectFromLibrary")
            }
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path d="M6 4v8l6-4-6-4z" fill="currentColor" />
            </svg>
          </button>
          <input
            id="qsbar-topic-video"
            className="qe-input qe-input--small"
            type="url"
            placeholder="https://..."
            value={topicVideo}
            onChange={(e) => onUpdateQuestion({ topicVideoUrl: e.target.value })}
          />
          <button
            type="button"
            className="qe-qsbar__mini-button"
            onClick={() =>
              openPreview({
                title: t("questionSettings.videoPreview"),
                url: normalizeUrl(topicVideo),
                type: "video",
              })
            }
            disabled={!topicVideo.trim()}
            title={t("questionSettings.preview")}
            aria-label={t("questionSettings.preview")}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" />
              <circle cx="8" cy="8" r="2" />
            </svg>
          </button>
        </div>
      </div>

      {/* Solution text (media library + URL) */}
      <div className="qe-qsbar__field qe-qsbar__field--url">
        <label className="qe-qsbar__label" htmlFor="qsbar-solution-text">
          {t("questionSettings.solutionTextUrl")}
        </label>
        <div className="qe-qsbar__url-row">
          <button
            type="button"
            className="qe-qsbar__mini-button qe-qsbar__mini-button--library"
            onClick={() => {
              setEducationalMaterialContext({
                onSelect: (url) => onUpdateQuestion({ solutionTextUrl: url }),
                currentUrl: solutionTextUrl,
              });
              setEducationalMaterialModalType("others");
              setIsEducationalMaterialModalOpen(true);
            }}
            disabled={
              !subTopic ||
              !educationalMaterials ||
              !Array.isArray(educationalMaterials.others) ||
              educationalMaterials.others.length === 0
            }
            title={t("props.videoSelectFromLibrary")}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path
                d="M9.5 2H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V5.5L9.5 2zM4 13V3h5v3h4v7H4z"
                fill="currentColor"
              />
            </svg>
          </button>
          <input
            id="qsbar-solution-text"
            className="qe-input qe-input--small"
            type="url"
            placeholder="https://..."
            value={solutionTextUrl}
            onChange={(e) =>
              onUpdateQuestion({ solutionTextUrl: e.target.value })
            }
          />
          <button
            type="button"
            className="qe-qsbar__mini-button"
            onClick={() =>
              openPreview({
                title: t("questionSettings.textPreview"),
                url: normalizeUrl(solutionTextUrl),
                type: "text",
              })
            }
            disabled={!solutionTextUrl.trim()}
            title={t("questionSettings.preview")}
            aria-label={t("questionSettings.preview")}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" />
              <circle cx="8" cy="8" r="2" />
            </svg>
          </button>
        </div>
      </div>

      {/* Time limit */}
      <div className="qe-qsbar__field qe-qsbar__field--time">
        <label className="qe-qsbar__label" htmlFor="qsbar-time-limit">
          {t("questionSettings.timeLimit")}
        </label>
        <input
          id="qsbar-time-limit"
          className="qe-input qe-input--small"
          type="number"
          min="0"
          placeholder={t("questionSettings.timeLimitPlaceholder")}
          value={timeLimit}
          onChange={(e) => handleTimeLimitChange(e.target.value)}
        />
      </div>

      {/* Toggles */}
      <div className="qe-qsbar__toggles">
        <label className="qe-qsbar__toggle">
          <input
            type="checkbox"
            checked={trackTime}
            onChange={(e) => onUpdateQuestion({ trackTime: e.target.checked })}
          />
          <span>{t("questionSettings.trackTime")}</span>
        </label>
        <label className="qe-qsbar__toggle">
          <input
            type="checkbox"
            checked={notMobileFriendly}
            onChange={(e) =>
              onUpdateQuestion({ notMobileFriendly: e.target.checked })
            }
          />
          <span>{t("questionSettings.notMobileFriendly")}</span>
        </label>
        <label className="qe-qsbar__toggle">
          <input
            type="checkbox"
            checked={displayInMba}
            onChange={(e) =>
              onUpdateQuestion({ displayInMba: e.target.checked })
            }
          />
          <span>{t("questionSettings.showInMbaExam")}</span>
        </label>
      </div>

      </div>

      {/* URL preview modal */}
      {previewState.isOpen && (
        <div className="qe-modal">
          <div
            className="qe-modal__overlay"
            onClick={closePreview}
            role="button"
            aria-label={t("questionSettings.close")}
          />
          <div
            className="qe-modal__content"
            role="dialog"
            aria-modal="true"
            tabIndex={-1}
            style={{ maxWidth: "700px", width: "100%", height: "70vh" }}
          >
            <header className="qe-modal__header">
              <div>
                <h2>{previewState.title}</h2>
                <p className="qe-modal__subtitle">
                  {t("questionSettings.urlPreview")}
                </p>
              </div>
              <button
                className="qe-button qe-button--ghost"
                type="button"
                onClick={closePreview}
              >
                {t("questionSettings.close")}
              </button>
            </header>
            <div
              className="qe-modal__body"
              style={{ height: "100%", paddingTop: 0 }}
            >
              {previewState.url ? (
                <>
                  <div style={{ marginBottom: "8px" }}>
                    <a
                      className="qe-link"
                      href={previewState.url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t("questionSettings.openInNewTab")}
                    </a>
                  </div>
                  <div
                    style={{
                      border: "1px solid var(--qe-border-subtle, #E5E7EB)",
                      borderRadius: "8px",
                      overflow: "hidden",
                      height: "calc(70vh - 180px)",
                    }}
                  >
                    {previewState.type === "video" ? (
                      isDirectVideoUrl(previewState.url) ? (
                        <video
                          controls
                          src={previewState.url}
                          style={{ width: "100%", height: "100%" }}
                        />
                      ) : (
                        <iframe
                          title={t("questionSettings.videoPreview")}
                          src={buildYoutubeEmbedUrl(previewState.url)}
                          style={{ width: "100%", height: "100%", border: "0" }}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      )
                    ) : (
                      <div
                        onClick={() => window.open(previewState.url, "_blank")}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            window.open(previewState.url, "_blank");
                          }
                        }}
                        style={{
                          width: "100%",
                          height: "100%",
                          cursor: "pointer",
                          position: "relative",
                        }}
                        title={t("questionSettings.openInNewTab")}
                        aria-label={t("questionSettings.openInNewTab")}
                      >
                        <iframe
                          title={t("questionSettings.textPreview")}
                          src={previewState.url}
                          style={{
                            width: "100%",
                            height: "100%",
                            border: "0",
                            pointerEvents: "none",
                          }}
                        />
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <p>{t("questionSettings.urlPreview")}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Educational material picker modal */}
      {isEducationalMaterialModalOpen && educationalMaterialContext && (
        <EducationalMaterialModal
          onClose={() => {
            setIsEducationalMaterialModalOpen(false);
            setEducationalMaterialContext(null);
          }}
          onSelect={(url) => {
            educationalMaterialContext.onSelect(url);
            setIsEducationalMaterialModalOpen(false);
            setEducationalMaterialContext(null);
          }}
          currentUrl={educationalMaterialContext.currentUrl}
          materials={educationalMaterials}
          type={educationalMaterialModalType}
        />
      )}
    </div>
  );
};

export default QuestionSettingsBar;
